// The Reports view keeps its selections in the URL's search params so that
// Back steps through them and a URL can be shared. A parameter that is absent
// (or invalid) means the caller's default.

export const readEnum = <T extends string>(
  value: string | null,
  options: readonly T[]
): T | undefined =>
  value !== null && (options as readonly string[]).includes(value)
    ? (value as T)
    : undefined;

// A yyyy-MM-dd parameter as a local calendar date (`new Date(string)` would
// read it as UTC midnight).
export const readDay = (value: string | null): Date | undefined => {
  if (value === null || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

// The partner selection: no `partner` parameter means every partner (null),
// a single empty `partner=` means none, otherwise one parameter per partner.
export const readPartners = (params: URLSearchParams): string[] | null => {
  const values = params.getAll('partner');
  if (values.length === 0) return null;
  return values.filter((name) => name !== '');
};

export const writePartners = (
  params: URLSearchParams,
  selected: string[] | null
): URLSearchParams => {
  const next = new URLSearchParams(params);
  next.delete('partner');
  if (selected !== null) {
    if (selected.length === 0) next.append('partner', '');
    selected.forEach((name) => next.append('partner', name));
  }
  return next;
};

// A copy of the params with one value set, or removed when undefined/empty.
export const withParam = (
  params: URLSearchParams,
  key: string,
  value: string | undefined
): URLSearchParams => {
  const next = new URLSearchParams(params);
  if (value === undefined || value === '') next.delete(key);
  else next.set(key, value);
  return next;
};
